import React, { useCallback, useEffect, useMemo, useState } from "react"
import { useNavigate, useParams } from "react-router-dom"
import { message, Spin } from "antd"
import { useSelector } from "react-redux"
import { jwtDecode } from "jwt-decode"
import { translations } from "../../utils/Translation/staticTranslation"
import { getUserCookies } from "../../utils/Cookies"
import {
  effectiveHighestRole,
  isPlansPricingReadOnly,
} from "../../utils/modulePermissionSidebar"
import { PlansApi } from "../../Apis/plansApi"
import {
  buildPlanCreatePayload,
  buildPlanUpdatePayload,
  getDefaultFeatureImageCosts,
  parsePlanFeaturesConfigFromApi,
  getPlansApiError,
  getPlansApiSuccessMessage,
  isPlansApiResponseSuccess,
  mapPlanDetailToForm,
  parsePlanFromApiResponse,
  getPlanFormRequiredFieldErrorKey,
  validatePlanFormFeatures,
} from "../../utils/plansPricingPayload"
import CreatePlanForm from "../../components/PlansPricing/CreatePlan/CreatePlanForm"
import CreatePlanPreview from "../../components/PlansPricing/CreatePlan/CreatePlanPreview"
import CreatePlanConfirmModal from "../../components/PlansPricing/CreatePlan/CreatePlanConfirmModal"
import "../../components/PlansPricing/CreatePlan/CreatePlan.css"

const INITIAL_PLAN_FORM = {
  planName: "",
  planNameArabic: "",
  description: "",
  descriptionArabic: "",
  planType: "Subscription",
  planCategory: "standard",
  billingCycle: "Monthly",
  price: "",
  priceUsd: "",
  images: "0",
  furnitureFinder: "0",
  discounted: false,
  discountedPrice: "",
  discountedPriceUsd: "",
  activeStatus: true,
  activeFeatureTab: "feature_access",
  features: [],
  allowedFeatures: [],
  featureImageCosts: getDefaultFeatureImageCosts(),
  planFeaturesConfig: null,
}

const CreatePlan = () => {
  const navigate = useNavigate()
  const { planId } = useParams()
  const isEditMode = Boolean(planId)
  const lang = localStorage.getItem("language") || "ar"
  const t = translations[lang]?.plansPricingPage
  const tCreate = t?.createPlan

  const user = useSelector((state) => state.user.user)
  const token = getUserCookies()?.token
  const decoded = token ? jwtDecode(token) : null
  const [form, setForm] = useState(INITIAL_PLAN_FORM)
  const [loadingPlan, setLoadingPlan] = useState(isEditMode)
  const [loadingFeatures, setLoadingFeatures] = useState(true)
  const [planFeaturesConfig, setPlanFeaturesConfig] = useState(null)
  const [saving, setSaving] = useState(false)
  const [activeModal, setActiveModal] = useState(null)

  const isReadOnly = isPlansPricingReadOnly(user, decoded)
  const isSuperAdmin =
    effectiveHighestRole(user, decoded?.highest_role) === "SUPERADMIN"

  const previewForm = useMemo(() => {
    const { planCategory: _adminCategory, ...userFacing } = form
    return userFacing
  }, [form])

  const goBack = () => navigate("/plans-pricing")

  useEffect(() => {
    if (isReadOnly) {
      message.info(
        t?.read_only_action_blocked ||
          "View only — you cannot perform this action."
      )
      navigate("/plans-pricing", { replace: true })
    }
  }, [isReadOnly, navigate, t?.read_only_action_blocked])

  const loadPlan = useCallback(async () => {
    if (!planId) return
    setLoadingPlan(true)
    try {
      const response = await PlansApi.getPlanById(planId)
      if (response?.status === 200) {
        const detail = parsePlanFromApiResponse(response.data)
        const mapped = mapPlanDetailToForm(detail)
        if (mapped) {
          setForm((prev) => ({
            ...mapped,
            planFeaturesConfig: prev.planFeaturesConfig,
            featureImageCosts: {
              ...getDefaultFeatureImageCosts(prev.planFeaturesConfig),
              ...mapped.featureImageCosts,
            },
          }))
        }
      }
    } catch (error) {
      console.error("Error loading plan:", error)
      message.error(
        getPlansApiError(error, lang) ||
          t?.error_fetching_plan ||
          "Error loading plan."
      )
      goBack()
    } finally {
      setLoadingPlan(false)
    }
  }, [planId, lang, t?.error_fetching_plan])

  useEffect(() => {
    if (isEditMode) loadPlan()
  }, [isEditMode, loadPlan])

  useEffect(() => {
    let cancelled = false
    const loadFeatures = async () => {
      setLoadingFeatures(true)
      try {
        const response = await PlansApi.getPlanFeatures()
        if (!cancelled && response?.status === 200) {
          const config = parsePlanFeaturesConfigFromApi(response.data)
          setPlanFeaturesConfig(config)
          setForm((prev) => ({
            ...prev,
            planFeaturesConfig: config,
            featureImageCosts: {
              ...getDefaultFeatureImageCosts(config),
              ...prev.featureImageCosts,
            },
          }))
        }
      } catch (error) {
        console.error("Error loading plan features:", error)
        if (!cancelled) setPlanFeaturesConfig(null)
      } finally {
        if (!cancelled) setLoadingFeatures(false)
      }
    }
    loadFeatures()
    return () => {
      cancelled = true
    }
  }, [])

  const closeModal = () => setActiveModal(null)

  const notifyReadOnly = () => {
    message.info(
      t?.read_only_action_blocked ||
        "View only — you cannot perform this action."
    )
  }

  const openModal = (variant) => {
    if (isReadOnly) {
      notifyReadOnly()
      return
    }
    if (variant === "confirm" && !form.planName.trim()) {
      message.warning(tCreate?.plan_name_required || "Plan name is required.")
      return
    }
    if (variant === "confirm" || variant === "draft") {
      const requiredFieldKey = getPlanFormRequiredFieldErrorKey(form)
      if (requiredFieldKey) {
        message.warning(
          tCreate?.[requiredFieldKey] || "Please complete all required fields."
        )
        return
      }
      if (validatePlanFormFeatures(form)) {
        message.warning(
          tCreate?.features_bilingual_required ||
            "Each feature requires both an English and an Arabic description."
        )
        return
      }
    }
    setActiveModal(variant)
  }

  const handleSave = async (mode) => {
    if (mode === "cancel") {
      closeModal()
      goBack()
      return
    }

    setSaving(true)
    try {
      const payload =
        isEditMode && planId
          ? buildPlanUpdatePayload(form, mode)
          : buildPlanCreatePayload(form, mode)

      const response =
        isEditMode && planId
          ? await PlansApi.updatePlan(planId, payload)
          : await PlansApi.createPlan(payload)

      if (isPlansApiResponseSuccess(response)) {
        closeModal()
        const savedPlan = parsePlanFromApiResponse(response.data)
        const draftFallbackEn = isEditMode
          ? tCreate?.plan_updated || "Plan updated successfully."
          : tCreate?.draft_saved || "Draft saved successfully."
        const draftFallbackAr = isEditMode
          ? "تم تحديث الباقة بنجاح."
          : "تم حفظ المسودة بنجاح."
        const confirmFallbackEn = isEditMode
          ? tCreate?.plan_updated || "Plan updated successfully."
          : tCreate?.plan_confirmed || "Plan created successfully."
        const confirmFallbackAr = isEditMode
          ? "تم تحديث الباقة بنجاح."
          : "تم إنشاء الباقة بنجاح."
        const successMsg = getPlansApiSuccessMessage(
          response.data,
          lang,
          mode === "draft" ? draftFallbackEn : confirmFallbackEn,
          mode === "draft" ? draftFallbackAr : confirmFallbackAr
        )

        if (mode === "draft") {
          const savedStatus = String(savedPlan?.status || "").toLowerCase()
          if (savedPlan && savedStatus && savedStatus !== "draft") {
            message.warning(
              tCreate?.draft_status_mismatch ||
                "Plan was saved, but status may not be Draft. Check the plans list."
            )
          } else {
            message.success(
              successMsg ||
                (isEditMode
                  ? tCreate?.plan_updated
                  : tCreate?.draft_saved) ||
                "Draft saved successfully."
            )
          }

          const savedId = savedPlan?.id ?? planId
          if (savedId) {
            if (!isEditMode) {
              navigate(`/plans-pricing/edit/${savedId}`, { replace: true })
            } else {
              const mapped = mapPlanDetailToForm(savedPlan)
              if (mapped) {
                setForm((prev) => ({
                  ...mapped,
                  planFeaturesConfig: prev.planFeaturesConfig,
                  featureImageCosts: {
                    ...getDefaultFeatureImageCosts(prev.planFeaturesConfig),
                    ...mapped.featureImageCosts,
                  },
                }))
              } else {
                loadPlan()
              }
            }
          } else if (!isEditMode) {
            navigate("/plans-pricing")
          }
        } else if (mode === "confirm") {
          message.success(
            successMsg ||
              (isEditMode
                ? tCreate?.plan_updated
                : tCreate?.plan_confirmed) ||
              "Plan saved successfully."
          )
          goBack()
        }
      } else {
        message.error(
          getPlansApiError(
            { response },
            lang
          ) || t?.error_saving_plan || "Error saving plan."
        )
      }
    } catch (error) {
      console.error("Error saving plan:", error)
      message.error(
        getPlansApiError(error, lang) ||
          t?.error_saving_plan ||
          "Error saving plan."
      )
    } finally {
      setSaving(false)
    }
  }

  if (isReadOnly || loadingPlan) {
    return (
      <div className="create-plan-page create-plan-page--loading">
        <Spin size="large" />
      </div>
    )
  }

  return (
    <div className="create-plan-page">
      <nav className="create-plan-breadcrumbs" aria-label="Breadcrumb">
        <span className="crumb-link" onClick={goBack} role="button" tabIndex={0}>
          {t?.title || "Pricing & Plans"}
        </span>
        <img src="/discounts/angle-right.svg" alt="" width={16} height={16} />
        <span className="crumb-current">
          {isEditMode
            ? tCreate?.edit_breadcrumb || "Edit Plan"
            : tCreate?.breadcrumb || "Create Plan"}
        </span>
      </nav>

      <header className="create-plan-page-header">
        <h1 className="create-plan-page-title">
          {isEditMode
            ? tCreate?.edit_page_title || "Edit Plan"
            : tCreate?.page_title || "Create New Plan"}
        </h1>
        <p className="create-plan-page-subtitle">
          {tCreate?.page_subtitle ||
            "Configure your pricing plan details and settings."}
        </p>
      </header>

      <div className="create-plan-layout">
        <div className="create-plan-form-area">
          <CreatePlanForm
            form={form}
            onChange={setForm}
            isReadOnly={isReadOnly}
            isEditMode={isEditMode}
            isSuperAdmin={isSuperAdmin}
            planFeaturesConfig={planFeaturesConfig}
            loadingFeatures={loadingFeatures}
          />
          <footer className="create-plan-footer">
            <button
              type="button"
              className="btn-cancel"
              onClick={() => openModal("cancel")}
            >
              {tCreate?.cancel || "Cancel"}
            </button>
            <button
              type="button"
              className="btn-draft"
              onClick={() => openModal("draft")}
              disabled={isReadOnly || saving}
            >
              {tCreate?.save_draft || "Save Draft"}
            </button>
            <button
              type="button"
              className="btn-confirm"
              onClick={() => openModal("confirm")}
              disabled={isReadOnly || saving}
            >
              {isEditMode
                ? tCreate?.update_plan || "Update Plan"
                : tCreate?.confirm_plan || "Confirm Plan"}
            </button>
          </footer>
        </div>
        <CreatePlanPreview form={previewForm} />
      </div>

      <CreatePlanConfirmModal
        open={activeModal === "confirm"}
        variant="confirm"
        onClose={closeModal}
        onConfirm={() => handleSave("confirm")}
        loading={saving}
        titleOverride={
          isEditMode ? tCreate?.update_plan || "Update Plan" : undefined
        }
        buttonOverride={
          isEditMode ? tCreate?.update_plan || "Update Plan" : undefined
        }
      />
      <CreatePlanConfirmModal
        open={activeModal === "draft"}
        variant="draft"
        onClose={closeModal}
        onConfirm={() => handleSave("draft")}
        loading={saving}
      />
      <CreatePlanConfirmModal
        open={activeModal === "cancel"}
        variant="cancel"
        onClose={closeModal}
        onConfirm={() => handleSave("cancel")}
        loading={saving}
      />
    </div>
  )
}

export default CreatePlan
